/**
 * WALL-FIRST TRAFFIC FLOW — the seeded threat spawner.
 *
 * There are no pattern groups and no random per-lane streams here. Traffic is
 * built AROUND a hidden GUARANTEED PATH: the path weaves between the three
 * lanes on a seeded cadence, and EVERY time it steps out of a lane the spawner
 * schedules a BLOCK — one or two vehicles, plus followers — into the lanes the
 * path is NOT in. The player is therefore forced to move at the path's cadence
 * (a lane change roughly every 1.7 s), the road is never empty, and the run is
 * still provably survivable at every instant.
 *
 * WHY IT WAS REWRITTEN. The previous model was SUBTRACTIVE: three independent
 * random per-lane arrival streams, with anything landing near the corridor
 * DELETED. Emptiness was structural — ~20% of a run had no lane that could
 * legally take even a car, long lulls survived every compensator (starvation
 * cap, burst cap, global cadence floor, dual-corridor density boost), and the
 * only vehicle that ever forced the player to move was the corridor close
 * behind them. Nothing here is generated-then-deleted: every placement is
 * CHOSEN legal, so there is no hole to compensate for and the compensator stack
 * is gone.
 *
 * The spawner is a pure function of (travel distance, player speed, director
 * state) plus one internal RNG stream seeded at construction, so identical seeds
 * reproduce identical runs and different seeds diverge.
 *
 * EVERY vehicle sits at an EXACT LANE CENTRE (x = laneCenterX(lane), i.e.
 * -2.7 / 0 / +2.7): the road is a pure arcade 3-lane grid and a threat is either
 * in your lane or it is not.
 *
 * ---------------------------------------------------------------------------
 * 1. THE CLOCK.
 *
 * Everything in this file is expressed in ARRIVAL TIME — the sim time at which a
 * vehicle's centre reaches the player plane (z = 0). `update()` is handed travel
 * distance and player speed, not time, so the spawner integrates its own clock
 * (`now += Δdistance / playerSpeed`). That is exact against world.ts (the same
 * speed drives both) and, unlike `director.time`, it still advances in the tests
 * that pin the director to a fixed difficulty.
 *
 * ---------------------------------------------------------------------------
 * 2. THE PATH (`pathLaneAt(t)`).
 *
 * The path is a piecewise-constant lane as a function of arrival time: the lane
 * the player is guaranteed to be able to occupy at time t. It steps to an
 * ADJACENT lane every `flow.pathTransitionMin .. pathTransitionMax`
 * (0.9-2.0 s), so it reads as a continuous slalom: 1 -> 0 -> 1 -> 2 -> 1 ... at
 * irregular intervals. THAT is the weave the player is forced into; it is never
 * displayed, only implied by where the traffic is.
 *
 * There is exactly ONE path. `pathBLaneAt(t)` survives on the interface (and on
 * `SpawnCommand.pathLaneB`) but always returns null: the DUAL-corridor mode of
 * the old model is gone. It protected two of the three lanes at once, which
 * meant two thirds of the road could not legally receive anything, and it was
 * the measured root cause of 97-99% of the old model's long all-clear stretches.
 *
 * THE BOUNCE FLOOR + THE ALTERNATION BIAS (`drawHold`). A lane can only receive
 * traffic while the path is out of it for longer than a full guard window,
 * 2 x (w + crossMargin) ~ 1.2-1.6 s (5). The path's excursion out of lane X
 * lasts one hold if the next transition BOUNCES straight back into X, or three
 * holds if it CROSSES to the far side. So:
 *   - a segment whose NEXT transition bounces back into the lane it just left
 *     holds for at least flow.pathBounceHoldMin (1.62 s — comfortably over one
 *     truck guard window at the SLOWEST closing speed of the run, 1.52 s, plus
 *     the discretization epsilon).
 *     From an outer lane the only adjacent lane is the middle one, so an outer
 *     hold is always a bounce and always gets the floor.
 *   - leaving the middle lane the path CROSSES to the far side rather than
 *     bouncing with probability flow.pathAlternateBias (0.75), which makes the
 *     weave full-width and makes the long three-hold window the common case.
 * Every excursion is therefore FEEDABLE, which is what lets the block model
 * below place its vehicles legally instead of skipping them.
 * The measured mean hold is ~1.67 s, i.e. a forced lane change every 1.67 s.
 *
 * THE OPENING. The first two path segments are placed deliberately, so that the
 * run's first vehicle (lane 1, arrival = traffic.firstGroupLeadTime = 2.0 s) is
 * legal under the path guard of (5) rather than by exemption:
 *   - the path leaves lane 1 at `2.0 - guard(truck) - 0.02` ≈ 1.20 s, clearing
 *     the START of that vehicle's window;
 *   - the second segment (an outer lane, whose only adjacent lane is 1 again)
 *     is held for at least `2.0 + guard(truck) + 0.02 - 1.20` ≈ 1.59 s, clearing
 *     the END of it. The hold is still a seeded draw, squeezed into
 *     [1.62, pathTransitionMax] (the bounce floor is the binding one at the
 *     current tuning) instead of [0.9, pathTransitionMax].
 * From the third segment on the path is unconstrained. The first transition
 * (at ≈1.20 s) schedules the run's first BLOCK exactly like any other.
 *
 * ---------------------------------------------------------------------------
 * 3. BLOCKS (the scheduling core).
 *
 * Let the path step at time t out of lane A into the adjacent lane B, and let
 * C be the third lane. Because B is adjacent to A, C is ALWAYS an outer lane
 * (0 or 2), and A is the middle lane on every other transition.
 *
 * One block is scheduled per transition:
 *
 *   PATTERN (one draw). FULL with probability flow.blockFullChance (0.93),
 *     LIGHT otherwise — except that two LIGHT blocks never run BACK TO BACK,
 *     because the third lane would then go two whole path holds (up to 4 s) with
 *     nothing in it. MEASURED: 6.5% of blocks are LIGHT.
 *   A-VEHICLE (always). Arrives at `t + uniform(blockCloseMin, blockCloseMax)`
 *     (0.32-0.72 s). This is the vehicle that seals the corridor behind the
 *     player: drifting back, or idling on a lane boundary, is never an option.
 *     It is NEVER skipped — if its lane is blocked it is moved to the first
 *     legal instant however far that is.
 *   C-VEHICLE (FULL only). Desired at the A-vehicle's desired time plus an
 *     independent jitter in ±flow.blockJitter (0.28 s). A FULL block is
 *     therefore a 2-wide wall with exactly one gap in it — the lane the path
 *     just moved into — and the player has to be in that gap.
 *
 * THE TWO CLAMPS. Both desired times are clamped into the window their own lane
 * actually has:
 *   - BELOW, for the A-vehicle only, by `t + pathGuard(kind) + epsilon`, so it
 *     can never catch a player still crossing OUT of A;
 *   - ABOVE, for both, by the path's NEXT ENTRY into that lane minus a truck
 *     guard. C is adjacent to B, so the path can step straight into it at the
 *     very next transition, and A is re-entered two transitions later; without
 *     this clamp the desired instant landed inside that entry's guard window
 *     about a fifth of the time. That mattered because legalization is
 *     FORWARD-ONLY (below): the next legal instant was then on the far side of
 *     the whole excursion, seconds later, so the A-vehicle sealed the lane long
 *     after the player had left it and the C-vehicle was skipped outright — the
 *     block silently degraded to LIGHT. Adding the clamp took the block-leader
 *     skip rate from 10.9% to 3.5%, the worst cross-lane gap from 2.9 s to
 *     2.3 s, and the stay-in-lane bot's worst survival after the path leaves its
 *     lane from 4.5 s to 1.7 s.
 *
 * LEGALIZATION, and why it is FORWARD-ONLY. A desired arrival that still lands
 * on the path's guard window in its own lane is moved to the first legal instant
 * at or after it (`earliestLegal`). Moving it EARLIER is not available at that
 * point: the same-lane headway floor and the committed kick wall have already
 * pushed the placement to the earliest instant the lane can physically accept,
 * so anything before it is illegal for a different reason — which is exactly why
 * the upper clamp above is applied to the DESIRED time instead. If the first
 * legal instant is more than flow.blockLegalizeMax (2.0 s, one full hold) past
 * the desired time the placement is SKIPPED: dropping it a whole hold later
 * would land it on top of the NEXT block rather than filling the hole it was
 * meant to fill. MEASURED skip rate: 4.3% of block leaders, 19.8% of followers
 * (almost all of them middle-lane followers, which genuinely do not fit — see
 * below).
 *
 * WHY THIS CANNOT GO EMPTY. Every transition yields 1-2 guaranteed arrivals plus
 * followers, and transitions come every ~1.67 s. The worst cross-lane hole is
 * therefore about one long hold, and it is bounded BY CONSTRUCTION rather than
 * by a compensator. MEASURED over 400 ramping + 400 max-difficulty seeds x 120 s
 * each, ignoring the opening 6 s:
 *
 *                    cross-lane arrival gap    zero visible   per-lane gap
 *                    worst  p99   mean         worst  p99     worst  mid mean
 *   SUBTRACTIVE ramp 3.59+  2.55  0.456        1.60   1.35    16.0   2.31
 *   SUBTRACTIVE max  3.34+  2.17  0.357        1.70   1.15    13.4   1.63
 *   WALL-FIRST ramp  2.36   1.71  0.458        0.00   0.00     6.2   2.00
 *   WALL-FIRST max   2.29   1.66  0.420        0.00   0.00     6.3   1.64
 *   DENSE ramping    2.24   1.36  0.335        0.00   0.00     6.0   1.64
 *   DENSE max        2.06   1.37  0.290        0.00   0.00     5.8   1.24
 *
 * The road is now NEVER empty: over 800 runs there is not one 0.05 s sample at
 * which no vehicle is inside the live fog horizon, against 1.7 s stretches
 * before. The wall-first rewrite left the mean cross-lane gap unchanged and
 * collapsed the TAIL, which was its whole point — the old stream fired
 * constantly into whatever lane happened to be legal, so its mean looked good
 * and its worst case was 3.6 s.
 *
 * THE DENSITY PASS (owner playtest: "still a bit too easy, increase the density
 * of traffic") then took the MEAN down 27% ramping / 31% at max, with the tail
 * still shrinking, purely through the volume levers — blockFullChance 0.8 ->
 * 0.93, followerChance 0.85 -> 0.95, followerChainMax 2 -> 3, followerJitterMax
 * 0.15 -> 0.05, blockClose 0.4-0.9 -> 0.32-0.72 and blockJitter 0.35 -> 0.28.
 * NOTHING in the solvability set moved: crossMargin, the path cadence
 * (pathTransitionMin/Max, pathBounceHoldMin, pathAlternateBias), bikeSpace,
 * spawnClearance, requiredHeadway and every kick-wall lever are untouched, and
 * all the bot sweeps below are green at the new density.
 *
 * THE MIDDLE LANE IS STRUCTURALLY THE THINNEST, and there is a hard floor on it.
 * Lane 1 is the lane every weave passes through, so the path sits in it about
 * half the time; it can only receive traffic in the window between two
 * consecutive visits, which is `hold - 2 x guard` — about 0.2 s at run start and
 * 0.6 s at full ramp. That is one vehicle per OUTER-lane hold (one per two
 * transitions, ~3.3 s) plus whatever followers fit in the same window, which is
 * why flow.blockCloseMin and flow.followerJitterMax are both small: an
 * A-vehicle placed at the FIRST legal instant leaves the rest of the window for
 * a follower. MEASURED mean gap 1.64 s ramping / 1.24 s at full ramp (2.01 /
 * 1.61 before the density pass, which is why blockClose and followerJitterMax
 * were trimmed with the follower levers: raising the FULL chance alone feeds
 * only lane C, which is always an OUTER lane, and it pushed the outer/middle
 * load ratio past 2). The only levers that could lower it further are
 * flow.crossMargin and the path cadence, and both ARE the solvability
 * guarantee.
 *
 * ---------------------------------------------------------------------------
 * 4. FOLLOWERS (what turns a block into traffic).
 *
 * Every scheduled vehicle independently gets a FOLLOWER behind it in the same
 * lane with probability flow.followerChance (0.95): arrival =
 * `leader + requiredHeadway + uniform(0, flow.followerJitterMax)`, legalized
 * exactly like a C-vehicle and skipped if it does not fit. A follower may get a
 * follower of its own, up to flow.followerChainMax (3) deep.
 *
 * Without them each lane receives exactly one vehicle per relevant transition,
 * the arrival stream is audibly periodic, and the middle lane is starved. The
 * nominal 0.95 is high on purpose: the headway floor and the path guard reject
 * the ones that do not fit, so the MEASURED rate is 1.82 emitted followers per
 * emitted leader. These two levers are where the density pass got most of its
 * volume — a queue behind a leader adds vehicles without touching the path
 * cadence or the guard, so `requiredHeadway` (6) remains the floor and a lane
 * is never packed tighter than the bike can thread.
 *
 * DENSITY. `director.density` scales flow.blockFullChance and
 * flow.followerChance by `min(1, density / traffic.densityStart)`, so density
 * remains a live volume control downward and saturates upward (at densityStart
 * the schedule is already one block per transition at the full follower rate).
 *
 * ---------------------------------------------------------------------------
 * 5. THE PATH GUARD (the solvability core).
 *
 * A vehicle in lane L at arrival t is LEGAL only if the path is out of L across
 *
 *     [t - w - c,  t + w + c]
 *
 *   w = (vehicleLength/2 + playerLongExtent) / closingSpeed
 *       — half the time the vehicle and the bike overlap longitudinally as it
 *       passes. `vehicleLength` is the LONGEST model of that kind (semi 11 m,
 *       suv 4.8 m), because the model is chosen from the pool slot at spawn time
 *       and is not knowable here; `playerLongExtent` is 1.45 m, the visible
 *       bike's front-wheel plane (contact.playerFrontZ).
 *   c = flow.crossMargin (0.55 s) — the reaction budget: one lane cross is
 *       2.7 / 12 = 0.225 s, so c is 2.4x the bare travel time, covering input
 *       latency, the real player's lateral inertia, and the ~0.05 s a vehicle
 *       gains on its planned arrival as the speed ramp climbs under it.
 *
 * Worked numbers at run start (closing 32.9 truck / 37.2 car):
 *     truck: w = (5.5 + 1.45) / 32.9 = 0.211 s -> window ±0.76 s
 *     car:   w = (2.4 + 1.45) / 37.2 = 0.103 s -> window ±0.65 s
 * At full ramp (68.9 / 76.0) those fall to ±0.65 s and ±0.60 s: the guard is
 * dominated by c, so the corridor stays a fixed-in-time hole while the metres it
 * spans grow with the ramp.
 *
 * The guard is the ONLY thing that ever moves or removes a placement, and it
 * does so BEFORE the vehicle exists — nothing is generated and then deleted.
 *
 * ---------------------------------------------------------------------------
 * 6. SAME-LANE HEADWAY (no interpenetration, ever).
 *
 * Two vehicles in one lane close on the player at different speeds when their
 * kinds differ (car 37.2 / truck 32.9 m/s at start), so arrival-time separation
 * alone does not keep them apart. For a leading vehicle (arrival t1, closing cl)
 * and a trailing one (t2 = t1 + Δt, closing ct), the separation is linear in
 * time: sep(τ) = cl·(τ - t1) - ct·(τ - t2). Two endpoints bound it:
 *
 *   (A) at the trailing vehicle's ARRIVAL: sep = cl·Δt, which must exceed
 *       halfLengths + flow.bikeSpace (6 m) — the bike may be threading that lane
 *       between them.
 *   (B) at the trailing vehicle's SPAWN (both spawn at the same time-to-arrival,
 *       see 7): sep = ct·lead - cl·(lead - Δt), which must exceed
 *       halfLengths + flow.spawnClearance (2 m). This only binds when cl > ct,
 *       i.e. a CAR scheduled just before a TRUCK — without it the truck is born
 *       in front of the car it is supposed to follow.
 *
 * `requiredHeadway` returns the max of the two, evaluated against the last
 * vehicle actually EMITTED in that lane. Worked numbers at run start: car→car
 * 0.29 s, truck→car 0.42 s, truck→truck 0.52 s, car→truck 1.11 s (the (B)
 * case — it grew with the 240 m spawn horizon, because `lead` is in it). At
 * full ramp: 0.14 / 0.20 / 0.25 / 0.46 s. It is a hard floor: it
 * outranks a block's desired time, and a follower that cannot clear it is simply
 * pushed later (and then skipped if that makes it illegal).
 *
 * ---------------------------------------------------------------------------
 * 7. PLACEMENT.
 *
 * A planned arrival becomes a SpawnCommand on the first frame where its
 * time-to-arrival falls inside the spawn envelope
 *
 *     leadTime = max(refClosing * traffic.minReactionTime,
 *                    traffic.spawnHorizonMin) / refClosing
 *
 * (refClosing = playerSpeed + truck speed, the slowest closing speed — so the
 * envelope is a horizon in the REFERENCE frame). leadTime is 7.29 s at run start
 * and 3.48 s at full ramp (the 240 m horizon term binds at every speed; the
 * minReactionTime term is 24.7 m / 51.7 m and never comes close). The command's
 * z is then
 *
 *     z = -(playerSpeed + ownSpeed) * (arrival - now)
 *
 * i.e. each vehicle is placed at ITS OWN closing speed, so the arrival time it
 * was planned for holds exactly and a truck is not overtaken by the car beside
 * it. A truck lands exactly on the 240 m horizon; a car, closing 13% faster,
 * spawns at ~271 m (265 m at full ramp) so that they arrive together.
 *
 * Nothing can pop in: planning always runs a full leadTime (plus slack) ahead of
 * `now`, so by the time an arrival is close enough to spawn it has existed for
 * seconds. The horizon is deliberately BEYOND the render's draw distance
 * (~200-210 m), which is what traffic.spawnHorizonMin buys and why it is
 * coordinated with the render side rather than tuned here. The pool has to be
 * sized for it too: 7.29 s of road at run start is a MEASURED worst case of 30
 * simultaneous live threats (see traffic.maxThreatVehicles — an undersized pool
 * silently drops spawn commands, kick-wall vehicles included).
 *
 * The one moment the pipeline is genuinely empty is t = 0, where the
 * whole opening 7.3 s of traffic is emitted on frame one — onto an empty screen,
 * which is exactly what makes the opening a stream instead of a lone car.
 *
 * PLANNING HORIZONS. Blocks are scheduled out to `now + leadTime + PLAN_SLACK`;
 * the PATH is planned a further PATH_SLACK beyond that, because legalizing a
 * C-vehicle or a follower has to know where the path goes NEXT (it may step into
 * C at the very next transition) and the kick-wall slot search needs more
 * lookahead still.
 *
 * ---------------------------------------------------------------------------
 * 8. WHY IT IS SURVIVABLE (the invariant, pinned by the path-bot sweeps in
 *    spawner.test.ts).
 *
 * The PATH BOT follows `pathLaneAt(now)` for the whole run, moving toward that
 * lane centre at lateralMaxSpeed. It never looks at traffic and never
 * anticipates a transition, so it is always ~0.225 s late to the new lane — the
 * crossing margin covers that. It kicks exactly one class of vehicle: the
 * 3-wide kick wall's vehicle in its own lane (11). If the model is correct it is
 * immortal:
 *
 *  - CONTACT. The bot is in the path lane P except while crossing after a
 *    transition, and by (5) no vehicle whose overlap window touches the
 *    transition time exists in either the lane it left or the lane it enters.
 *    It is 0.225 s late; the guard reserves at least 0.55 s on both sides.
 *  - SQUEEZE (world.ts). Resting on a lane centre, the bot is never STRICTLY
 *    between two vehicle centres one lane apart — it is ON one of the two
 *    candidate centres, and the far pair (5.4 m) is excluded by
 *    SQUEEZE_MAX_SEPARATION. While crossing from A to B its x lies strictly
 *    inside (A, B) only, and both A and B are empty across that window by (5).
 *  - NO SAME-LANE PILE-UPS. By (6), so "the path lane is clear" is a statement
 *    about a lane that actually has room in it.
 *
 * FORCED WEAVE — the other half of the invariant, and the point of the rewrite.
 * A STAY-IN-LANE bot in any lane dies: whenever the path leaves that lane the
 * block's A-vehicle arrives 0.4-0.9 s later, and whenever the path is elsewhere
 * the lane is either B (which the bot is not in) or C (which a FULL block
 * fills). MEASURED over 800 seeds x 3 lanes: every stay-in-lane bot dies within
 * 4.3 s of the run start and within 1.7 s of the path leaving its lane. The path
 * bot's own forced lane-change cadence is 1.67 s (mean), i.e. exactly the path
 * transition cadence.
 *
 * ---------------------------------------------------------------------------
 * 9. ANTI-CAMP (why hugging a shoulder dies).
 *
 * The strafe clamp is road.strafeHalfWidth = 3.85 m. A LANE-CENTRED vehicle in
 * an outer lane (centre |x| = 2.7) covers out to 2.7 + contactHalfWidth, where
 * contactHalfWidth = modelWidth/2 + playerHalfWidth(0.30) − forgiveness(0.15):
 *     semi      2.6 wide -> 1.45 -> reaches 4.15  >= 3.85  KILLS a clamped player
 *     boxtruck  2.5      -> 1.40 -> reaches 4.10  >= 3.85  KILLS
 *     suv       1.95     -> 1.125-> reaches 3.825 (2.5 cm short)
 *     sedan     1.85     -> 1.075-> reaches 3.775 (7.5 cm short)
 *     hatchback 1.75     -> 1.025-> reaches 3.725 (12.5 cm short)
 * So only a TRUCK punishes edge camping. Blocks fill each outer lane constantly,
 * but WHICH kind lands there is a draw (truckRatio ramps 0.15 -> 0.4). To make
 * it a guarantee, each outer lane tracks the arrival time of the last truck
 * actually emitted there; once flow.outerTruckInterval (12 s) has passed, the
 * next vehicle scheduled into that lane is upgraded to a truck (`drawKind`). The
 * kind draw is always taken, so the upgrade never shifts the RNG stream, and the
 * timer only resets when a truck is actually EMITTED — an upgrade the legalizer
 * skips re-arms it. MEASURED over 120 seeds x 180 s: worst first truck 15.4 s,
 * worst interval between trucks in one outer lane 16.9 s (the old subtractive
 * model measured 24.1 / 22.3 on the same sweep).
 *
 * ---------------------------------------------------------------------------
 * 10. RANDOMNESS BUDGET (all from the one seeded stream, drawn in a fixed order:
 *     the path is extended first, then any kick-wall commit for this update,
 *     then the block stream in transition order):
 *   - PATH: 1 draw per transition for its hold, 1 for the lane it steps into.
 *   - the FIRST kick wall's time: 1 draw, taken at the very start of the block
 *     stream (immediately before the opening vehicle's kind draw).
 *   - the OPENING vehicle: 1 kind draw (through `scheduleArrival`).
 *   - per BLOCK, in this exact order:
 *       1. the PATTERN (FULL vs LIGHT),
 *       2. the A-delay,
 *       3. the A-vehicle's KIND — taken inside `scheduleArrival`, always, even
 *          if the placement is skipped, so a skip cannot shift the stream,
 *       4. the C-jitter — taken UNCONDITIONALLY, even on a LIGHT block, so the
 *          pattern draw does not change the stream's shape,
 *       5. the C-vehicle's KIND (FULL blocks only — the pattern draw already
 *          decided this deterministically),
 *       6. the follower chain behind the A-vehicle, then the one behind the
 *          C-vehicle: per level, 1 chance draw and then — only if it succeeded —
 *          1 jitter draw and 1 kind draw. A chain stops at the first failed
 *          chance draw, at flow.followerChainMax, or at the first skipped
 *          placement (a follower behind nothing is meaningless).
 *   - the MIDDLE-LANE TRUCK DOWNGRADE and the ANTI-CAMP upgrade take NO draw:
 *     both only reinterpret the kind draw that was already taken.
 *   - KICK WALLS: per COMMITTED wall, in this order — 1 ALIGNED-PAIR draw (which
 *     adjacent pair, 0+1 or 1+2), 3 kind draws (lanes 0,1,2 in order; the pair's
 *     two are then resolved to one shared kind), 3 STAGGER draws (lanes 0,1,2 in
 *     order — unit fractions, stretched over the live unslippable span
 *     afterwards, so the clamp cannot shift the stream; the pair shares one
 *     offset, so one of the three is spare) and 1 for the next wall's interval.
 *     A wall whose slot search fails takes NO draw at all, so retrying it on a
 *     later update cannot shift the stream.
 *
 * ---------------------------------------------------------------------------
 * 11. KICK WALLS (the one forced kick) — 3 WIDE, and not to be confused with the
 *     2-wide BLOCKS of (3), which always leave the path's lane open.
 *
 * Every flow.wall.intervalMin..intervalMax seconds — first at
 * flow.wall.firstArrival plus a small jitter — ALL THREE LANES receive a vehicle
 * at (very nearly) the same arrival time. This is the ONLY permitted violation
 * of the path guard (5): the path's lane gets one too, so there is no line
 * through the wall and the only answer is to KICK the vehicle in your own lane.
 * A wall command carries `wall: true` and is the one case where
 * `pathLane === lane`.
 *
 * The intended shape is: ride the path as usual (ordinary block vehicles before
 * and after the wall are still kept out of the path's lane, so it is clear on
 * both sides), kick the one vehicle blocking it, carry on.
 *
 * THE ALIGNED PAIR (owner playtest: "vehicles don't appear close enough for a
 * double kick — make 3-wide walls have 2 vehicles aligned"). TWO of the three
 * wall vehicles arrive TOGETHER: a seeded per-wall draw picks the adjacent pair
 * (lanes 0+1 or 1+2), and those two share one arrival time AND one KIND. The
 * shared kind is what makes the alignment real: every vehicle is placed at its
 * OWN closing speed (header 7), so two DIFFERENT kinds planned for one arrival
 * time spawn metres apart and only converge at z = 0 — they would sit far
 * outside kick.pairMaxDz (2.5 m) for the whole approach, i.e. exactly through
 * the kick window. Equal kinds close at equal speed, so the pair's z gap is
 * EXACTLY 0 from spawn to contact and the wall is answerable with ONE DOUBLE
 * KICK by a player straddling that boundary (sim/kick.ts pickKickTargets).
 * A mixed kind draw resolves to TRUCK, so the anti-camp upgrade (9) survives.
 *
 * The wall stays solvable exactly as before for a lane-CENTRED player: the pair
 * is a plain 2-wide slice of the wall, and the path lane's vehicle — whether it
 * is in the pair or is the third one — is still answered by one ordinary kick.
 * Straddling is an OPTION, never a requirement.
 *
 * THE STAGGER, AND WHY IT IS STILL UNSLIPPABLE (owner: "they don't have to spawn
 * at the exact same instant — stagger them slightly, as long as you can't slip
 * past them"). The THIRD vehicle draws an offset in
 * [wall.pairSepMin, 1] x span against the pair's, and one draw decides which of
 * the two arrives first, so the wall lands as a shallow ripple rather than a
 * ruled line. All offsets still live in [0, span], which is all the bound below
 * assumes. The pairSepMin floor keeps the ALIGNED pair unambiguous: the third
 * vehicle is never within a metre of one of them.
 *
 * Vehicle l is lethal to a player in lane l across [a_l - w_l, a_l + w_l],
 *     w_l = (kindLength(kind_l)/2 + PLAYER_LONG_EXTENT) / closing(kind_l)
 * — half the time it overlaps the bike longitudinally (the guard of (5) WITHOUT
 * the crossing margin). Claim: the wall is unslippable as long as the windows of
 * ADJACENT lanes still OVERLAP. Suppose they do, and take a player in lane i who
 * wants to reach adjacent lane j:
 *   - they cannot stay in i (their own vehicle arrives), so they must be out of
 *     i before a_i - w_i;
 *   - if they arrive in j at or before a_j - w_j they are simply in j when j's
 *     vehicle lands — and the third lane is covered too, so there is nowhere to
 *     go on from there;
 *   - if they arrive in j after a_j + w_j, their crossing interval [d, d + L]
 *     satisfies d <= a_i - w_i and d + L >= a_j + w_j, so it CONTAINS the whole
 *     of D_i ∩ D_j — and during that intersection they are strictly between two
 *     lane centres with a vehicle spanning them on each side, which is exactly
 *     what world.ts's SQUEEZE rule kills. No kick answers a squeeze.
 *   - a two-lane sweep 0 -> 2 is the same argument through lane 1.
 * Adjacent overlap means |a_i - a_j| <= w_i + w_j, and every offset lives in
 * [0, span], so
 *
 *     span <= 2 * min_l w_l          (minus one GUARD_EPSILON so the windows
 *                                     strictly overlap rather than touch)
 *
 * is sufficient for all pairs at once. The derivation never needed the
 * lane-cross time: one lane cross is LANE_CROSS_TIME = 0.225 s, LONGER than the
 * whole 2w window (0.11-0.46 s) it would have to land inside, so the crossing
 * cost is pure extra margin on top of the bound.
 *
 * w SHRINKS AS THE RAMP CLIMBS (0.211 s for a start-speed truck, 0.101 s for a
 * full-ramp truck, 0.103 / 0.051 s for a car), so the bound tightens through the
 * run and `wallStaggerCap` recomputes it LIVE at every commit from the kinds
 * actually drawn. flow.wall.staggerMax (0.25 s) is only a ceiling on it.
 *
 * PLACEMENT RULES (`wallSlot`, `commitWall`):
 *   - intervalMin (20 s) is 4x kick.cooldown, so two walls can never both land
 *     inside one cooldown. Slides only ever push them further apart.
 *   - the PATH must hold ONE lane across the wall's overlap window (plus the
 *     kick window and one lane cross before it, because the player is that late
 *     to a transition), so the player is parked on a lane CENTRE while the three
 *     vehicles pass. A player between two lane centres with a vehicle on each
 *     side dies to world.ts's SQUEEZE rule, which no kick can answer.
 *   - per-lane headway floors still apply, both behind (traffic already
 *     scheduled) and ahead (a block vehicle that cannot keep headway in front of
 *     a committed wall is skipped, `fitWall`).
 *   - kinds are ordinary per-lane draws, anti-camp upgrade included.
 *
 * A wall is COMMITTED out of order — early, WALL_COMMIT_LEAD inside the planned
 * path horizon — because its slot search needs lookahead. The block stream keeps
 * scheduling right up to it, and `absorbWall` folds it into each lane's headway
 * state as that lane's clock passes it. MEASURED over 400 + 400 seeds x 120 s:
 * 3.92 walls per ramping run and 3.98 per max-difficulty run, never fewer than
 * 3 inside a 120 s window, never closer together than intervalMin minus one
 * stagger span.
 */

import type { Rng } from "../core/rng";
import { mulberry32 } from "../core/rng";
import { TUNING } from "./tuning";
import type { DirectorState } from "./director";

/** A single vehicle to spawn, in sim coordinates. */
export interface SpawnCommand {
  kind: "car" | "truck";
  /** Corridor lane index 0..2. The vehicle sits at that lane's EXACT centre. */
  lane: number;
  /**
   * The guaranteed path lane at this vehicle's arrival time — a lane the player
   * can always be in when it passes. Never equal to `lane`, EXCEPT on a KICK
   * WALL command (`wall === true`), which is the one deliberate violation of the
   * path guard (see header 11). Consumers may ignore it; it exists so the
   * solvability invariant is observable from the command stream alone.
   */
  pathLane: number;
  /**
   * ALWAYS NULL. The old dual-corridor model had a second protected path here;
   * it was removed (header 2). The field survives so consumers and the recorded
   * command shape do not have to change.
   */
  pathLaneB: number | null;
  /** Spawn z (negative, metres ahead of the player). */
  z: number;
  /** Absolute vehicle speed v_veh (m/s, oncoming). */
  speed: number;
  /**
   * True for the three vehicles of a KICK WALL event (header 11): all three
   * lanes at nearly the same arrival time, including the path's lane.
   * Absent/false otherwise.
   */
  wall?: boolean;
}

/** Lateral center of corridor lane index 0..2. */
function laneCenterX(lane: number): number {
  return (lane - 1) * TUNING.road.laneWidth;
}

const LANES = [0, 1, 2] as const;
const F = TUNING.flow;

/** The lane the player starts in, and where the path begins. */
export const PATH_START_LANE = 1;

/**
 * Longest CAR / TRUCK model (m). The visual model is picked from the pool slot
 * at spawn time (vehicles.ts pickVehicleModel), so the spawner cannot know which
 * one a command will become — every longitudinal safety margin here therefore
 * uses the worst case for the kind.
 */
export const MAX_CAR_LENGTH = Math.max(
  TUNING.vehicleModels.sedan.length,
  TUNING.vehicleModels.suv.length,
  TUNING.vehicleModels.hatchback.length,
);
export const MAX_TRUCK_LENGTH = Math.max(
  TUNING.vehicleModels.boxtruck.length,
  TUNING.vehicleModels.semi.length,
);
/** Shortest CAR / TRUCK model (m) — see `minKindLength`. */
export const MIN_CAR_LENGTH = Math.min(
  TUNING.vehicleModels.sedan.length,
  TUNING.vehicleModels.suv.length,
  TUNING.vehicleModels.hatchback.length,
);
export const MIN_TRUCK_LENGTH = Math.min(
  TUNING.vehicleModels.boxtruck.length,
  TUNING.vehicleModels.semi.length,
);

/** Worst-case model length (m) for a kind. */
export function kindLength(kind: "car" | "truck"): number {
  return kind === "truck" ? MAX_TRUCK_LENGTH : MAX_CAR_LENGTH;
}

/**
 * SHORTEST model of a kind (m) — the other worst case.
 *
 * Every safety margin that asks "how long could this vehicle be?" uses
 * `kindLength` (the longest model), because a longer vehicle is a longer
 * danger window. The UNSLIPPABLE STAGGER BOUND (header 11) asks the opposite
 * question — "how SHORT could the overlap window be?" — since a wall stays
 * unslippable only while adjacent lanes' windows still overlap, and the shortest
 * model gives the narrowest window. Using the longest model there let a wall of
 * three hatchbacks (4.0 m against the 4.8 m suv the bound assumed) spread wide
 * enough to be threaded.
 */
export function minKindLength(kind: "car" | "truck"): number {
  return kind === "truck" ? MIN_TRUCK_LENGTH : MIN_CAR_LENGTH;
}

/**
 * How far the VISIBLE bike extends from the camera plane, m. The front wheel
 * (contact.playerFrontZ = -1.45) is the far edge, so this is what a vehicle has
 * to clear before and after z = 0 to be past the player.
 */
export const PLAYER_LONG_EXTENT = Math.max(
  -TUNING.contact.playerFrontZ,
  TUNING.contact.playerBackZ,
);

/** Time to cross one lane at max lateral speed (s) — 2.7 / 12 = 0.225. */
export const LANE_CROSS_TIME =
  TUNING.road.laneWidth / TUNING.player.lateralMaxSpeed;

/**
 * Half the time a vehicle of `kind` overlaps the bike longitudinally, plus the
 * crossing margin: the radius of the arrival-time window that must be free of
 * the path for this vehicle to be legal. See header (5).
 */
export function pathGuard(kind: "car" | "truck", closingSpeed: number): number {
  return (
    (kindLength(kind) / 2 + PLAYER_LONG_EXTENT) / closingSpeed + F.crossMargin
  );
}

/**
 * Discretization epsilon (s) added whenever an arrival is placed exactly at the
 * edge of a guard window (the opening vehicle, and every block A-vehicle), so a
 * float-equal boundary can never read as an overlap.
 */
const GUARD_EPSILON = 0.02;

/**
 * Extra seconds planned beyond the spawn envelope. Arrivals must be decided
 * before they need to spawn; this is the headroom that keeps that true across a
 * shrinking envelope (leadTime falls from 5.35 s to 2.62 s over the ramp).
 */
const PLAN_SLACK = 1.5;

/**
 * Extra seconds of PATH planned beyond the block planning limit. Covers guard
 * windows, the block-close delay, headway pushes, the legalizer's forward walk
 * and the kick-wall slot search, so nothing is ever committed into a stretch of
 * time whose path is still undecided.
 */
const PATH_SLACK = 20;

/**
 * How far inside the planned path horizon a KICK WALL is committed (s).
 *
 * A wall is committed EARLY — well ahead of the block stream's own planning
 * limit — because its slot search needs path lookahead, and because its backward
 * headway is then measured against traffic that has only been scheduled to
 * `now + leadTime`. The remaining PATH_SLACK - this is the room the slot search
 * has to work in.
 */
const WALL_COMMIT_LEAD = 8;

/**
 * Safety factor on the UNSLIPPABLE STAGGER BOUND (header 11).
 *
 * The bound is computed when the wall is COMMITTED, which is 10-15 s of arrival
 * time before it lands, and it divides by the closing speed at that moment. The
 * speed ramp keeps climbing in between, so the real overlap window at arrival is
 * a few percent SHORTER than the one the bound assumed. 0.85 covers that with
 * room to spare (the ramp moves the closing speed by at most ~7% over a wall's
 * commit lead) and costs only a slightly tighter ripple.
 */
const WALL_STAGGER_SAFETY = 0.85;

/** Nudge used when an event time has to be strictly after another. */
const TIME_EPSILON = 1e-6;

/** Arrivals closer than this are dropped rather than spawned on top of the player. */
const MIN_SPAWN_LEAD = 0.05;

/** Safety cap on planning iterations per update (never reached in practice). */
const MAX_PLAN_STEPS = 4096;

/** Whether `lane` is one of the two outer lanes (the camp-able ones). */
function isOuterLane(lane: number): boolean {
  return lane === 0 || lane === 2;
}

/** One constant-lane stretch of the guaranteed path, in arrival-time space. */
interface PathSegment {
  start: number;
  end: number;
  lane: number;
}

/** A planned (not yet spawned) arrival. */
interface PlannedArrival {
  arrival: number;
  lane: number;
  kind: "car" | "truck";
  pathLane: number;
  wall?: boolean;
}

/**
 * Everything ONE path transition schedules (header 3). Queued by `stepPath` in
 * chronological order and consumed by `planTo`.
 */
interface BlockRequest {
  /** The instant the path left `laneA` (for the adjacent lane B). */
  at: number;
  /** The lane the path just vacated. Always receives a vehicle. */
  laneA: number;
  /** The third lane — never the path's. Receives one on a FULL block. */
  laneC: number;
}

/**
 * How a placement answers the path guard when its desired instant is blocked.
 *   "close"    — never skipped; walk forward to the first legal instant however
 *                far that is (the block's A-vehicle, which MUST seal the lane
 *                the player just left).
 *   "legalize" — walk forward at most flow.blockLegalizeMax, else skip.
 */
type PlaceMode = "close" | "legalize";

export interface Spawner {
  /**
   * Advance the spawner by the player's travel and return every vehicle whose
   * spawn moment has arrived. Deterministic given the seed and the call
   * sequence.
   *
   * @param distance total metres travelled this run (monotonic).
   * @param playerSpeed current player forward speed (m/s).
   * @param director current director snapshot.
   */
  update(
    distance: number,
    playerSpeed: number,
    director: DirectorState,
  ): SpawnCommand[];
  /**
   * The guaranteed path lane at sim time `t` — a lane the player may always
   * occupy at that instant. Pure: consumes no randomness and plans nothing, so
   * it is only meaningful for times the spawner has already planned (anything up
   * to `now` + the spawn envelope, which is every time a caller can be at).
   * Times beyond the planned horizon report the path's current tail lane.
   */
  pathLaneAt(t: number): number;
  /**
   * ALWAYS NULL — the dual-corridor model is gone (header 2). Kept so callers
   * and recorded streams do not have to change.
   */
  pathBLaneAt(t: number): number | null;
}

/**
 * Create a seeded spawner. The RNG stream is `mulberry32(seed)`; the same seed
 * reproduces the same traffic across runs.
 */
export function createSpawner(seed: number): Spawner {
  const rng: Rng = mulberry32(seed);

  // --- clock (see header 1) ---
  let started = false;
  let now = 0;
  let prevDistance = 0;
  let prevSpeed: number = TUNING.player.speedStart;

  // --- the guaranteed path (see header 2) ---
  /** Closed segments, oldest first. The tail lane extends from `pathTime` on. */
  const path: PathSegment[] = [];
  let pathLaneCur = PATH_START_LANE;
  let pathTime = 0;
  let pathStarted = false;
  /** The lane the path held BEFORE its current one (the bounce rules). */
  let pathPrevLane = -1;
  /** The lane the path will step into next — decided a segment ahead. */
  let pathNextLane = -1;
  /** One-shot extra floor on the next hold (the opening construction). */
  let pendingHoldFloor = 0;
  /** Time the path is planned through (nothing may be committed past this). */
  let plannedUntil = 0;

  // --- emit state, per lane (headway + anti-camp) ---
  /** Arrival time of the last vehicle EMITTED in each lane (-Infinity = none). */
  const lastEmitArrival = [-Infinity, -Infinity, -Infinity];
  const lastEmitKind: Array<"car" | "truck"> = ["car", "car", "car"];
  /** Arrival time of the last TRUCK emitted in each lane (anti-camp, header 9). */
  const lastTruckArrival = [0, 0, 0];

  /**
   * Was the PREVIOUS block LIGHT? Two LIGHT blocks never run back to back: the
   * third lane would then go two whole path holds (up to 4 s) without anything,
   * which is exactly the kind of hole this model exists to remove. The pattern
   * draw is still taken unconditionally, so the rule costs no RNG discipline.
   */
  let prevLight = false;

  /** Blocks awaiting scheduling, in transition order (header 3). */
  const blocks: BlockRequest[] = [];
  /** Planned arrivals not yet spawned, in arrival order. */
  const pending: PlannedArrival[] = [];
  /** The run's opening vehicle (lane 1 at firstGroupLeadTime) is forced once. */
  let firstArrivalDone = false;
  /**
   * Arrival time the next KICK WALL wants (header 11). Infinity until the block
   * stream starts, where the first one is drawn; re-drawn every time a wall is
   * actually placed. A wall whose slot is still past the planned path horizon
   * parks here at `plannedUntil` and is retried next update.
   */
  let wallNext = Infinity;
  /**
   * PER-LANE arrival times of the wall already COMMITTED to the stream
   * (Infinity = none). The three are within the committed wall's effective
   * stagger of each other, so every headway interaction is lane-local.
   */
  const wallArrival = [Infinity, Infinity, Infinity];
  /** The committed wall's per-lane kinds (for the headway it imposes). */
  const wallKinds: Array<"car" | "truck"> = ["car", "car", "car"];

  // Reusable command buffer to avoid per-call allocation of the array itself.
  const out: SpawnCommand[] = [];

  /** Absolute oncoming speed for a kind at the current director state. */
  function speedFor(kind: "car" | "truck", director: DirectorState): number {
    return kind === "truck"
      ? director.trafficSpeeds.truck
      : director.trafficSpeeds.car;
  }

  /** Closing speed (player + vehicle) for a kind. */
  function closingFor(
    kind: "car" | "truck",
    playerSpeed: number,
    director: DirectorState,
  ): number {
    return playerSpeed + speedFor(kind, director);
  }

  /**
   * The DENSITY lever as a multiplier on flow.blockFullChance and
   * flow.followerChance (header 4). Saturates at 1: at densityStart the schedule
   * is already one block per transition at the full follower rate, so density is
   * a thinning lever only.
   */
  function flowScale(director: DirectorState): number {
    const d =
      director.density > 0 ? director.density : TUNING.traffic.densityStart;
    return Math.min(1, d / TUNING.traffic.densityStart);
  }

  // -------------------------------------------------------------------------
  // The path timeline (header 2)
  // -------------------------------------------------------------------------

  /** The path lane at time `t` (see the Spawner interface doc). */
  function pathLaneAt(t: number): number {
    if (t >= pathTime) return pathLaneCur;
    for (let i = path.length - 1; i >= 0; i--) {
      const s = path[i];
      if (t >= s.start) return s.lane;
    }
    return path.length > 0 ? path[0].lane : pathLaneCur;
  }

  /** The removed second corridor. Always null (header 2). */
  function pathBLaneAt(_t: number): number | null {
    return null;
  }

  /**
   * THE BOUNCE FLOOR — why the path sometimes has to stay away longer.
   *
   * A lane can only receive traffic while the path is out of it for longer than
   * a full guard window, 2 x (w + crossMargin) ~ 1.2-1.6 s (header 5). The
   * path's excursion out of lane X lasts one hold if the next transition bounces
   * straight back into X, or three holds if it crosses to the far side. Drawn
   * flat from [0.9, 2.0] a bounce window is too short to admit anything about
   * two thirds of the time, so:
   *   - a segment whose NEXT transition bounces back into the lane it just left
   *     holds for at least flow.pathBounceHoldMin (1.62 s, a whole truck guard
   *     window plus the discretization epsilon at the SLOWEST closing speed of
   *     the run). From an outer lane the only adjacent lane is the middle one,
   *     so an outer hold is always a bounce and always gets the floor.
   *   - leaving the middle lane, the path CROSSES to the far side rather than
   *     bouncing with probability flow.pathAlternateBias (0.75), which both
   *     makes the weave full-width and makes the long three-hold window the
   *     common case.
   * A crossing middle-lane hold keeps the full 0.9 s floor, so the weave still
   * flicks; the measured hold mean is ~1.67 s.
   */
  function drawHold(bounce: boolean, extraFloor: number): number {
    const lo = Math.max(
      bounce ? F.pathBounceHoldMin : F.pathTransitionMin,
      F.pathTransitionMin,
      extraFloor,
    );
    const hi = Math.max(lo, F.pathTransitionMax);
    return lo + rng() * (hi - lo);
  }

  /**
   * Choose the lane the path steps into from `from`, having come from `back`
   * (-1 if unknown). Always consumes exactly one draw.
   */
  function chooseNextLane(from: number, back: number): number {
    const adjacent: number[] = LANES.filter((l) => Math.abs(l - from) === 1);
    const r = rng();
    if (adjacent.length === 1) return adjacent[0];
    const b = adjacent.indexOf(back) >= 0 ? back : -1;
    if (b < 0) return adjacent[Math.min(1, Math.floor(r * 2))];
    const across = adjacent.find((l) => l !== b) as number;
    return r < F.pathAlternateBias ? across : b;
  }

  /** Queue the BLOCK a transition out of `vacated` into `entered` schedules. */
  function queueBlock(vacated: number, entered: number, at: number): void {
    blocks.push({ at, laneA: vacated, laneC: 3 - vacated - entered });
  }

  /**
   * Close the path's current segment and step to an adjacent lane, queueing the
   * BLOCK that fills the lanes the path is not in.
   */
  function stepPath(): void {
    // The hold of the CURRENT segment can only be drawn once the NEXT lane is
    // known (the bounce floor depends on it), so the path always runs one
    // transition ahead of itself: `pathNextLane` is decided when a segment
    // opens, its length when the segment closes.
    const end =
      pathTime + drawHold(pathNextLane === pathPrevLane, pendingHoldFloor);
    pendingHoldFloor = 0;
    const vacated = pathLaneCur;
    path.push({ start: pathTime, end, lane: vacated });
    pathPrevLane = vacated;
    pathLaneCur = pathNextLane;
    pathTime = end;
    pathNextLane = chooseNextLane(pathLaneCur, pathPrevLane);
    queueBlock(vacated, pathLaneCur, end);
  }

  /**
   * Extend the path past `untilT`. The FIRST transition is placed so the run's
   * opening vehicle (lane 1 at firstGroupLeadTime) is legal — see header (2);
   * every later one is a plain seeded draw.
   */
  function ensurePath(
    untilT: number,
    playerSpeed: number,
    director: DirectorState,
  ): void {
    if (!pathStarted) {
      pathStarted = true;
      // Worst case over kinds: a truck's window is the widest.
      const guard = pathGuard("truck", closingFor("truck", playerSpeed, director));
      const first = TUNING.traffic.firstGroupLeadTime;
      const firstEnd = Math.max(
        F.pathTransitionMin,
        first - guard - GUARD_EPSILON,
      );
      // Segment 1 is placed, not drawn: lane 1 from t = 0 until firstEnd.
      path.push({ start: 0, end: firstEnd, lane: PATH_START_LANE });
      pathPrevLane = PATH_START_LANE;
      pathTime = firstEnd;
      pathLaneCur = chooseNextLane(PATH_START_LANE, -1); // an outer lane
      pathNextLane = chooseNextLane(pathLaneCur, pathPrevLane); // back to lane 1
      queueBlock(PATH_START_LANE, pathLaneCur, firstEnd);
      // The opening vehicle's window runs to `first + guard`, and from an outer
      // lane the path's ONLY adjacent lane is 1 again — so segment 2 has to
      // outlast that window or the opening vehicle would be illegal under its
      // own path guard. It is a floor on the seeded draw, not a replacement of
      // it (and it sits just below the bounce floor at the current tuning).
      pendingHoldFloor = first + guard + GUARD_EPSILON - firstEnd;
    }
    while (pathTime <= untilT) stepPath();
    // Drop segments that can no longer be queried or overlapped.
    while (path.length > 1 && path[0].end < now - 1) path.shift();
    plannedUntil = untilT;
  }

  // -------------------------------------------------------------------------
  // Path occupancy (the path guard, header 5)
  // -------------------------------------------------------------------------

  /**
   * If the path occupies `lane` anywhere in [t0, t1], the latest time any of
   * those occupancies ends (Infinity for the open tail); null when the lane is
   * free across the whole window.
   */
  function occupiedUntil(lane: number, t0: number, t1: number): number | null {
    let end: number | null = null;
    if (pathLaneCur === lane && t1 >= pathTime) return Infinity;
    for (let i = path.length - 1; i >= 0; i--) {
      const s = path[i];
      if (s.end <= t0) break;
      if (s.lane === lane && s.start < t1 && s.end > t0) {
        if (end === null || s.end > end) end = s.end;
      }
    }
    return end;
  }

  /**
   * The earliest time >= `t` at which the path OCCUPIES `lane` (t itself if it
   * already does; Infinity if it does not within the planned timeline). Used to
   * pull a block's C-vehicle in front of the path's next entry into C.
   */
  function pathEnters(lane: number, t: number): number {
    for (let i = 0; i < path.length; i++) {
      const s = path[i];
      if (s.end <= t || s.lane !== lane) continue;
      return Math.max(s.start, t);
    }
    if (pathLaneCur === lane) return Math.max(pathTime, t);
    return Infinity;
  }

  /** Whether the path occupies `lane` anywhere in [t0, t1]. */
  function pathOccupies(lane: number, t0: number, t1: number): boolean {
    return occupiedUntil(lane, t0, t1) !== null;
  }

  /**
   * The earliest time >= `from` at which a vehicle of guard radius `guard` may
   * legally arrive in `lane` (i.e. the path is out of it across the whole guard
   * window). Infinity if that is past the planned path horizon.
   */
  function earliestLegal(lane: number, from: number, guard: number): number {
    let t = from;
    for (let i = 0; i < 64; i++) {
      const end = occupiedUntil(lane, t - guard, t + guard);
      if (end === null) return t;
      if (!Number.isFinite(end)) return Infinity;
      t = end + guard + GUARD_EPSILON;
      if (t > plannedUntil) return Infinity;
    }
    return Infinity;
  }

  // -------------------------------------------------------------------------
  // Placement
  // -------------------------------------------------------------------------

  /**
   * Car or truck for a vehicle scheduled into `lane` at `arrival`.
   *
   * The RNG draw is unconditional so the anti-camp guarantee can never shift the
   * stream; it can only upgrade a car to a truck when the outer lane it lands in
   * has gone flow.outerTruckInterval without one actually being emitted.
   */
  function drawKind(
    lane: number,
    arrival: number,
    director: DirectorState,
  ): "car" | "truck" {
    const drewTruck = rng() < director.truckRatio;
    const starved =
      isOuterLane(lane) && arrival - lastTruckArrival[lane] >= F.outerTruckInterval;
    return drewTruck || starved ? "truck" : "car";
  }

  /**
   * Minimum arrival-time separation (s) between a leading vehicle of `leadKind`
   * and a trailing one of `trailKind` in the SAME lane. See header (6).
   */
  function requiredHeadway(
    leadKind: "car" | "truck",
    trailKind: "car" | "truck",
    playerSpeed: number,
    director: DirectorState,
    leadTime: number,
  ): number {
    const cl = closingFor(leadKind, playerSpeed, director);
    const ct = closingFor(trailKind, playerSpeed, director);
    const halves = (kindLength(leadKind) + kindLength(trailKind)) / 2;
    // (A) room for the bike between them when the trailing one reaches z = 0.
    let dt = (halves + F.bikeSpace) / cl;
    // (B) no interpenetration at the moment the trailing one spawns.
    if (cl > ct) {
      dt = Math.max(dt, (halves + F.spawnClearance + leadTime * (cl - ct)) / cl);
    }
    return dt;
  }

  /** Insert a planned arrival keeping `pending` sorted by arrival time. */
  function insertPending(item: PlannedArrival): void {
    let i = pending.length;
    while (i > 0 && pending[i - 1].arrival > item.arrival) i--;
    pending.splice(i, 0, item);
  }

  /**
   * Schedule ONE vehicle into `lane` at (at least) `desired`, applying the
   * same-lane headway floor, the committed kick wall, the path guard and the
   * anti-camp upgrade.
   *
   * Returns the arrival time actually used, or null when the placement was
   * SKIPPED. Exactly one kind draw is taken either way (header 10), so a skip
   * can never shift the RNG stream.
   */
  function scheduleArrival(
    lane: number,
    desired: number,
    playerSpeed: number,
    director: DirectorState,
    leadTime: number,
    mode: PlaceMode,
    /**
     * Time the path LEFT this lane, for a block's A-vehicle: the arrival is
     * pushed out to clear that transition's guard window, so the closing vehicle
     * can never catch a player still crossing out of the lane.
     */
    pathLeftAt?: number,
  ): number | null {
    // A committed WALL (header 11) is a real vehicle in this lane at
    // `wallArrival[lane]`, committed ahead of the planner's clock. Once the
    // lane's own clock passes it, it becomes the lane's last emitted vehicle for
    // headway purposes.
    if (desired >= wallArrival[lane]) absorbWall(lane);
    /**
     * Where a vehicle of kind `k` would actually land: the transition clamp, the
     * same-lane headway floor and the committed wall, in that order. `wallDrop`
     * means it cannot keep headway in front of the wall at all.
     */
    const place = (
      k: "car" | "truck",
    ): { guard: number; arrival: number; wallDrop: boolean } => {
      const guard = pathGuard(k, closingFor(k, playerSpeed, director));
      let a = desired;
      if (pathLeftAt !== undefined) {
        a = Math.max(a, pathLeftAt + guard + GUARD_EPSILON);
      }
      if (lastEmitArrival[lane] > -Infinity) {
        a = Math.max(
          a,
          lastEmitArrival[lane] +
            requiredHeadway(lastEmitKind[lane], k, playerSpeed, director, leadTime),
        );
      }
      const f = fitWall(lane, a, k, playerSpeed, director, leadTime);
      return { guard, arrival: f === null ? a : f, wallDrop: f === null };
    };
    const blockedBy = (p: {
      guard: number;
      arrival: number;
      wallDrop: boolean;
    }): boolean =>
      p.wallDrop || pathOccupies(lane, p.arrival - p.guard, p.arrival + p.guard);

    let kind = drawKind(lane, desired, director);
    let spot = place(kind);
    // MIDDLE-LANE TRUCK DOWNGRADE. A truck's guard window is ~0.24 s wider than
    // a car's, and the middle lane's legal windows are the narrowest on the road
    // (header 3): the path is in lane 1 about half the time, and an excursion
    // out of it clears a car's window far more often than a truck's. So a
    // MIDDLE-lane truck the path guard would reject is demoted to the car it
    // would otherwise have been, rather than the slot being lost. It costs
    // nothing:
    //   - it never widens a guard window, so it can never make a placement less
    //     safe (a car's window is strictly inside the truck's it replaces);
    //   - the ANTI-CAMP truck guarantee (header 9) only applies to the OUTER
    //     lanes, which are untouched — only a truck in an outer lane can reach
    //     a shoulder camper;
    //   - the car/truck draw is still taken unconditionally, so the RNG stream
    //     is unchanged.
    if (lane === 1 && kind === "truck" && blockedBy(spot)) {
      const alt = place("car");
      if (!blockedBy(alt)) {
        kind = "car";
        spot = alt;
      }
    }
    const kindGuard = spot.guard;
    let arrival = spot.arrival;
    if (spot.wallDrop) return null; // cannot fit in front of the committed wall
    if (pathOccupies(lane, arrival - kindGuard, arrival + kindGuard)) {
      // LEGALIZATION (header 3). Walk forward to the first legal instant. It is
      // forward-only because the headway floor and the wall have already pushed
      // this placement to the earliest instant the lane can physically accept.
      const rescued = earliestLegal(lane, arrival, kindGuard);
      if (!Number.isFinite(rescued)) return null;
      if (mode === "legalize" && rescued - desired > F.blockLegalizeMax) {
        return null;
      }
      arrival = rescued;
      // The move has to be reconciled with the wall all over again — and if that
      // pushes it back onto the path, it is skipped: the guard always wins.
      const refit = fitWall(lane, arrival, kind, playerSpeed, director, leadTime);
      if (refit === null) return null;
      if (refit !== arrival) {
        arrival = refit;
        if (pathOccupies(lane, arrival - kindGuard, arrival + kindGuard)) {
          return null;
        }
      }
    }
    insertPending({
      arrival,
      lane,
      kind,
      pathLane: pathLaneAt(arrival),
    });
    lastEmitArrival[lane] = arrival;
    lastEmitKind[lane] = kind;
    if (kind === "truck") lastTruckArrival[lane] = arrival;
    return arrival;
  }

  /**
   * The FOLLOWER chain behind the leader just placed in `lane` (header 4).
   *
   * Per level: one chance draw, and only if it succeeds one jitter draw and (in
   * `scheduleArrival`) one kind draw. The chain stops at the first failed chance
   * draw, at flow.followerChainMax, or at the first skipped placement.
   */
  function scheduleFollowers(
    lane: number,
    playerSpeed: number,
    director: DirectorState,
    leadTime: number,
  ): void {
    const chance = F.followerChance * flowScale(director);
    for (let depth = 0; depth < F.followerChainMax; depth++) {
      if (rng() >= chance) return;
      const leaderArrival = lastEmitArrival[lane];
      const leaderKind = lastEmitKind[lane];
      // The follower's own kind is not drawn yet, so the headway baseline is
      // estimated from the leader's kind; `scheduleArrival` re-applies the exact
      // floor once the kind is known, so this only shapes the jitter's origin.
      const nominal = requiredHeadway(
        leaderKind,
        leaderKind,
        playerSpeed,
        director,
        leadTime,
      );
      const desired = leaderArrival + nominal + rng() * F.followerJitterMax;
      const placed = scheduleArrival(
        lane,
        desired,
        playerSpeed,
        director,
        leadTime,
        "legalize",
      );
      if (placed === null) return;
    }
  }

  /**
   * Schedule one BLOCK: the vehicles one path transition puts on the road
   * (header 3). The draw order is fixed and documented in header 10.
   */
  function emitBlock(
    b: BlockRequest,
    playerSpeed: number,
    director: DirectorState,
    leadTime: number,
  ): void {
    const scale = flowScale(director);
    const full = rng() < F.blockFullChance * scale || prevLight;
    prevLight = !full;
    const delay = F.blockCloseMin + rng() * (F.blockCloseMax - F.blockCloseMin);
    // BOTH vehicles are pulled in front of the path's NEXT ENTRY into their own
    // lane. The path re-enters A after two more transitions at the earliest (it
    // has to come back through B), but it can step straight into C at the very
    // next one — and either way the desired instant t + 0.4..0.9 s can sit
    // inside that entry's guard window. Walking FORWARD from there is useless:
    // the next legal instant is on the far side of the whole excursion, seconds
    // later, so the A-vehicle used to seal the lane long after the player had
    // left it and the C-vehicle was simply skipped (the block silently degraded
    // to LIGHT). Clamping the desired time to the last instant that CLEARS the
    // entry keeps both. The clamp uses the TRUCK guard (the widest), so it is
    // valid whichever kind the draw produces, and the floor inside
    // `scheduleArrival` (pathLeftAt + guard) still wins when the window between
    // the two path visits is too narrow to hold anything at all.
    const truckGuard = pathGuard(
      "truck",
      closingFor("truck", playerSpeed, director),
    );
    const latest = (lane: number): number =>
      pathEnters(lane, b.at + TIME_EPSILON) - truckGuard - GUARD_EPSILON;
    const desiredA = Math.min(b.at + delay, latest(b.laneA));
    const placedA = scheduleArrival(
      b.laneA,
      desiredA,
      playerSpeed,
      director,
      leadTime,
      "close",
      b.at,
    );
    // Drawn UNCONDITIONALLY so a LIGHT block does not shift the stream.
    const jitter = (rng() * 2 - 1) * F.blockJitter;
    const placedC = full
      ? scheduleArrival(
          b.laneC,
          Math.min(desiredA + jitter, latest(b.laneC)),
          playerSpeed,
          director,
          leadTime,
          "legalize",
        )
      : null;
    if (placedA !== null) {
      scheduleFollowers(b.laneA, playerSpeed, director, leadTime);
    }
    if (placedC !== null) {
      scheduleFollowers(b.laneC, playerSpeed, director, leadTime);
    }
  }

  // -------------------------------------------------------------------------
  // KICK WALLS (header 11)
  // -------------------------------------------------------------------------

  /**
   * The earliest instant >= `from` at which a kick wall may land, or Infinity
   * when there is no such instant inside the planned path. A slot must sit
   * inside ONE path segment across [t - back, t + fwd], so a player riding the
   * path is parked on a lane CENTRE for as long as the three vehicles overlap
   * the bike longitudinally. A player caught between two lane centres with a
   * vehicle on each side is killed by world.ts's SQUEEZE rule, which no kick can
   * answer; and "kick the vehicle in YOUR lane" is only a well-posed instruction
   * while the player is in a lane.
   *
   * Only CLOSED segments are considered: the open tail past `pathTime` has not
   * decided its next transition yet, so a wall placed there could be split by a
   * transition committed on a later update.
   */
  function wallSlot(from: number, back: number, fwd: number): number {
    for (let i = 0; i < path.length; i++) {
      const s = path[i];
      if (s.end <= from) continue;
      const c = Math.max(from, s.start + back);
      if (c + fwd <= s.end) return c;
    }
    return Infinity;
  }

  /**
   * Fold the COMMITTED wall into `lane`'s emit state, once the lane's own clock
   * has passed it. A wall is committed AHEAD of the planner's clock (its slot
   * needs path lookahead), but for headway purposes it is simply the last
   * vehicle emitted in each of the three lanes.
   */
  function absorbWall(lane: number): void {
    const at = wallArrival[lane];
    if (!Number.isFinite(at) || lastEmitArrival[lane] >= at) return;
    lastEmitArrival[lane] = at;
    lastEmitKind[lane] = wallKinds[lane];
    if (wallKinds[lane] === "truck") lastTruckArrival[lane] = at;
  }

  /**
   * Reconcile an ordinary arrival with the COMMITTED wall (header 11).
   *
   *   - landing BEFORE the wall: it must keep a full headway in front of it, or
   *     it is skipped (`null`) — it has nowhere to go, and the wall is about to
   *     fill that lane anyway.
   *   - landing AT or AFTER a wall this lane had not reached yet (the headway
   *     floor and the legalizer can both push an arrival through one): the wall
   *     is folded in and becomes the vehicle this one follows.
   */
  function fitWall(
    lane: number,
    arrival: number,
    kind: "car" | "truck",
    playerSpeed: number,
    director: DirectorState,
    leadTime: number,
  ): number | null {
    const at = wallArrival[lane];
    if (!Number.isFinite(at)) return arrival;
    if (arrival < at) {
      const need = requiredHeadway(
        kind,
        wallKinds[lane],
        playerSpeed,
        director,
        leadTime,
      );
      return arrival + need > at ? null : arrival;
    }
    if (lastEmitArrival[lane] >= at) return arrival;
    absorbWall(lane);
    const floor =
      at + requiredHeadway(wallKinds[lane], kind, playerSpeed, director, leadTime);
    return arrival < floor ? floor : arrival;
  }

  /**
   * COMMIT the KICK WALL `wallNext` is asking for, and re-arm the next one.
   *
   * A wall is committed the moment it becomes due, but at the arrival time its
   * SLOT search returns, which may be seconds later — so unlike every other
   * arrival it is placed out of order, ahead of the planner's clock. Resolving
   * the slot needs path LOOKAHEAD (a stable segment long enough to hold the
   * whole staggered window), which the in-order block stream does not have.
   *
   * The wall is inserted into `pending` immediately and remembered in
   * `wallArrival` / `wallKinds`; the block stream keeps scheduling right up to
   * it, `scheduleArrival` skips whatever cannot keep headway ahead of it, and
   * `absorbWall` turns it into each lane's "last emitted" as that lane's clock
   * passes it. The backward headway (against traffic ALREADY emitted) is folded
   * into the slot search using the worst-case trailing kind, so that check is
   * draw-free and the kinds drawn afterwards can only need less room.
   */
  function commitWall(
    playerSpeed: number,
    director: DirectorState,
    leadTime: number,
  ): void {
    const truckClosing = closingFor("truck", playerSpeed, director);
    // Half the time the longest vehicle overlaps the bike (the guard WITHOUT
    // the crossing margin) — exactly how long a squeeze could last.
    const half = (MAX_TRUCK_LENGTH / 2 + PLAYER_LONG_EXTENT) / truckClosing;
    // The player has to be SETTLED in its lane before the KICK WINDOW opens,
    // not merely before the vehicle touches them: the window is only
    // max(kick.windowAhead, closing * kick.windowAheadTime) of approach — about
    // 0.22 s — and a player still crossing into the lane when it opens has no
    // chance to use it. The budget is kick.wallSettleTime, NOT the kinematic
    // LANE_CROSS_TIME (0.225 s): the real player crosses under lateralAccel /
    // lateralDamping physics, overshoots the lane centre by up to ~0.5 m and
    // needs the damping to bleed that off before they are actually parked over
    // the centre. Measured in-engine (bang-bang input, seed 42): ~0.65 s from
    // transition to stably-centred. A wall placed 0.8 s after a forced
    // transition was legal under the old margin and humanly unanswerable.
    const kickWindow = Math.max(
      TUNING.kick.windowAheadTime,
      TUNING.kick.windowAhead / truckClosing,
    );
    const back = half + kickWindow + TUNING.kick.wallSettleTime + GUARD_EPSILON;
    // The three vehicles arrive in [t, t + effectiveStagger]; the effective
    // stagger is not known until the kinds are drawn (below), so the SLOT search
    // uses the static ceiling, which is always >= it. The path therefore holds
    // one lane across the whole staggered window, and the path-lane vehicle's
    // own offset can never push its overlap window past the stable stretch.
    const fwd = half + F.wall.staggerMax + GUARD_EPSILON;
    let base = wallNext;
    for (const l of LANES) {
      if (lastEmitArrival[l] === -Infinity) continue;
      const worst = Math.max(
        requiredHeadway(lastEmitKind[l], "car", playerSpeed, director, leadTime),
        requiredHeadway(lastEmitKind[l], "truck", playerSpeed, director, leadTime),
      );
      base = Math.max(base, lastEmitArrival[l] + worst);
    }
    const t = wallSlot(base, back, fwd);
    if (!Number.isFinite(t)) {
      // No legal slot inside the planned path yet. Step just past this frame's
      // commit threshold and retry on the NEXT update, when the horizon and the
      // stream have both moved on. No draw is taken, so a retry cannot shift the
      // RNG stream.
      wallNext = plannedUntil - WALL_COMMIT_LEAD + TIME_EPSILON;
      return;
    }
    // The previous wall is behind every lane by now (this one is at least
    // flow.wall.intervalMin later): fold it in before it is overwritten.
    for (const l of LANES) absorbWall(l);
    // THE ALIGNED PAIR (header 11, DOUBLE KICK). One draw picks WHICH adjacent
    // pair is aligned — lanes 0+1 or 1+2 — so the double-kickable boundary moves
    // around the road from wall to wall instead of always being the same one.
    const pairLo = rng() < 0.5 ? 0 : 1;
    const pairHi = pairLo + 1;
    /** The one lane that is NOT in the pair (always an outer lane). */
    const third = pairLo === 0 ? 2 : 0;
    // One kind draw per lane, in lane order, always taken (RNG budget).
    const kinds: Array<"car" | "truck"> = [
      drawKind(0, t, director),
      drawKind(1, t, director),
      drawKind(2, t, director),
    ];
    // THE PAIR SHARES ONE KIND, and it is the TRUCKIER of the two draws.
    //
    // Same kind ⇒ same lane speed ⇒ the pair's z gap stays EXACTLY 0 from spawn
    // to the kick moment. Two different kinds scheduled at one arrival time
    // spawn metres apart (each vehicle is placed at its OWN closing speed,
    // header 7) and converge only at z = 0, so they would sit far outside
    // kick.pairMaxDz for the whole approach and the pair would never be
    // double-kickable — alignment in TIME is only alignment in SPACE for equal
    // kinds.
    //
    // Resolving a mixed draw UPWARD (truck wins) rather than to a fixed lane
    // keeps the ANTI-CAMP guarantee intact: `drawKind` upgrades a starved outer
    // lane to a truck, and that upgrade must survive into the wall or an outer
    // lane could go past flow.outerTruckInterval without one. Both draws are
    // still taken, so the RNG stream is untouched.
    const pairKind: "car" | "truck" =
      kinds[pairLo] === "truck" || kinds[pairHi] === "truck" ? "truck" : "car";
    kinds[pairLo] = pairKind;
    kinds[pairHi] = pairKind;
    // ...then one STAGGER draw per lane, in lane order, also always taken. The
    // raw draws are unit fractions; the SPAN they are stretched over is the
    // live unslippable bound (see wallStaggerCap), so the RNG stream is
    // independent of the speed ramp and of the clamp.
    const frac = [rng(), rng(), rng()];
    const span = wallStaggerCap(kinds, playerSpeed, director);
    // The pair shares ONE offset; the THIRD vehicle keeps the ripple. Its
    // separation is drawn over [pairSepMin, 1] x span rather than [0, 1] so the
    // aligned pair can never be confused with an accidentally-close third
    // vehicle — the wall always reads as "two abreast, one offset" — and one
    // draw decides which of the two arrives FIRST, so the ripple still runs both
    // ways. Both offsets stay inside [0, span], which is all the unslippable
    // bound (and the slot search's `fwd` margin) ever assumed.
    const sep =
      (F.wall.pairSepMin + (1 - F.wall.pairSepMin) * frac[third]) * span;
    const pairFirst = frac[pairLo] < 0.5;
    const offsets = [0, 0, 0];
    offsets[pairLo] = pairFirst ? 0 : sep;
    offsets[pairHi] = offsets[pairLo];
    offsets[third] = pairFirst ? sep : 0;
    const pa = pathLaneAt(t);
    for (const l of LANES) {
      const at = t + offsets[l];
      insertPending({
        arrival: at,
        lane: l,
        kind: kinds[l],
        pathLane: pa,
        wall: true,
      });
      wallKinds[l] = kinds[l];
      wallArrival[l] = at;
    }
    wallNext =
      t + F.wall.intervalMin + rng() * (F.wall.intervalMax - F.wall.intervalMin);
  }

  /**
   * THE UNSLIPPABLE STAGGER BOUND (header 11).
   *
   * Half the time a wall vehicle of `kind` overlaps the bike longitudinally is
   *     w = (kindLength(kind)/2 + PLAYER_LONG_EXTENT) / closing(kind)
   * and its lethal window is [arrival - w, arrival + w]. A wall stays
   * unslippable as long as the windows of ADJACENT lanes still overlap: a player
   * who leaves lane i before its window opens is then strictly between two lane
   * centres while BOTH vehicles span them longitudinally, which world.ts's
   * SQUEEZE rule kills, and a player who waits for lane j's window to end can no
   * longer clear lane i's. Adjacent overlap requires |o_i - o_j| <= w_i + w_j,
   * and every offset lives in [0, span], so
   *
   *     span <= 2 * min_l w_l
   *
   * is sufficient for all three pairs at once. On top of that, one lane cross
   * costs LANE_CROSS_TIME = 0.225 s — longer than the whole window it would have
   * to land inside (2w is 0.11-0.46 s) — so the bound below is the strict one
   * and the crossing time is pure extra margin.
   *
   * w shrinks as the ramp climbs, so the bound is computed LIVE at every commit
   * from the kinds actually drawn and the current closing speeds; flow.wall
   * .staggerMax is only a ceiling on it.
   */
  function wallStaggerCap(
    kinds: Array<"car" | "truck">,
    playerSpeed: number,
    director: DirectorState,
  ): number {
    let wMin = Infinity;
    for (const l of LANES) {
      const w =
        (minKindLength(kinds[l]) / 2 + PLAYER_LONG_EXTENT) /
        closingFor(kinds[l], playerSpeed, director);
      if (w < wMin) wMin = w;
    }
    // GUARD_EPSILON keeps the adjacent windows STRICTLY overlapping rather than
    // merely touching at a single instant, which a 60 Hz sim could step over;
    // WALL_STAGGER_SAFETY covers the one term the commit cannot know exactly,
    // the speed ramp between here and the wall's arrival ten-odd seconds later
    // (a faster closing speed is a SHORTER overlap window).
    return Math.max(
      0,
      Math.min(F.wall.staggerMax, 2 * wMin * WALL_STAGGER_SAFETY - GUARD_EPSILON),
    );
  }

  // -------------------------------------------------------------------------
  // The block stream
  // -------------------------------------------------------------------------

  /**
   * Schedule every BLOCK whose transition falls at or before `limit`, in
   * transition order so the RNG stream follows the timeline.
   */
  function planTo(
    limit: number,
    playerSpeed: number,
    director: DirectorState,
    leadTime: number,
  ): void {
    // The run's opening vehicle: lane 1 (where the player starts) at exactly
    // firstGroupLeadTime. The path has already left lane 1 by then, so it is
    // legal by construction (header 2) — it still goes through the same checks.
    if (!firstArrivalDone) {
      firstArrivalDone = true;
      // The FIRST wall time is the block stream's very first draw, so it sits at
      // a fixed, documented place in the RNG budget (header 10). Its jitter is
      // deliberately SMALL (a third of intervalMin) rather than the full
      // interval spread: three walls have to fit inside a 120 s run even after
      // the slot search has slid them later.
      wallNext = F.wall.firstArrival + (rng() * F.wall.intervalMin) / 3;
      scheduleArrival(
        PATH_START_LANE,
        TUNING.traffic.firstGroupLeadTime,
        playerSpeed,
        director,
        leadTime,
        "close",
      );
    }
    for (let step = 0; step < MAX_PLAN_STEPS; step++) {
      if (blocks.length === 0 || blocks[0].at > limit) return;
      emitBlock(blocks.shift() as BlockRequest, playerSpeed, director, leadTime);
    }
  }

  // -------------------------------------------------------------------------
  // update
  // -------------------------------------------------------------------------

  function update(
    distance: number,
    playerSpeed: number,
    director: DirectorState,
  ): SpawnCommand[] {
    out.length = 0;

    // --- clock (header 1) ---
    if (!started) {
      started = true;
      now = playerSpeed > 0 ? distance / playerSpeed : 0;
    } else if (prevSpeed > 0) {
      now += (distance - prevDistance) / prevSpeed;
    }
    prevDistance = distance;
    prevSpeed = playerSpeed;

    // --- spawn envelope (header 7) ---
    const refClosing =
      playerSpeed + Math.min(director.trafficSpeeds.car, director.trafficSpeeds.truck);
    const envelope = Math.max(
      refClosing * TUNING.traffic.minReactionTime,
      TUNING.traffic.spawnHorizonMin,
    );
    const leadTime = envelope / refClosing;

    // --- plan ---
    const planLimit = now + leadTime + PLAN_SLACK;
    ensurePath(planLimit + PATH_SLACK, playerSpeed, director);
    // KICK WALLS (header 11) are committed here, ahead of the block stream and
    // deep inside the path horizon — see WALL_COMMIT_LEAD.
    if (wallNext <= plannedUntil - WALL_COMMIT_LEAD) {
      commitWall(playerSpeed, director, leadTime);
    }
    planTo(planLimit, playerSpeed, director, leadTime);

    // --- emit everything inside the envelope ---
    while (pending.length > 0 && pending[0].arrival - now <= leadTime) {
      const item = pending.shift() as PlannedArrival;
      const tta = item.arrival - now;
      if (tta < MIN_SPAWN_LEAD) continue; // defensive: never spawn on the player
      const speed = speedFor(item.kind, director);
      out.push({
        kind: item.kind,
        lane: item.lane,
        pathLane: item.pathLane,
        pathLaneB: null,
        z: -(playerSpeed + speed) * tta,
        speed,
        ...(item.wall ? { wall: true as const } : {}),
      });
    }

    return out;
  }

  return { update, pathLaneAt, pathBLaneAt };
}

// Re-export lane geometry for tests/consumers.
export { laneCenterX };
